import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, CheckCircle2, MapPin } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { money, gcalLink } from "../../lib/helpers";
import { StatusBadge } from "../../components/Common";
import { Button } from "../../components/ui/button";
import PublicFrame, { usePublicBranding } from "./PublicFrame";
import HomeVisitExtras from "./HomeVisitExtras";
import { ContactButtons } from "../../components/ContactButtons";

export default function ManageBooking() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get(`/public/booking/${token}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  usePublicBranding(data);

  const cancel = async () => {
    if (!window.confirm(t("cancel_booking") + "?")) return;
    try { await api.post(`/public/booking/${token}/cancel`); toast.success(t("cancelled_ok")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (error) return <PublicFrame><p data-testid="manage-error">{error}</p></PublicFrame>;
  if (!data) return <PublicFrame><p>…</p></PublicFrame>;
  const a = data.appointment;

  return (
    <PublicFrame branding={data.branding}>
      {params.get("new") && a.status === "confirmed" && (
        <div className="flex items-center gap-3 rounded-[var(--radius)] border border-emerald-500/30 bg-emerald-500/10 p-4 mb-6 text-emerald-800" data-testid="manage-confirmed-banner">
          <CheckCircle2 className="h-5 w-5" />{t("booking_confirmed")}
        </div>
      )}
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t("manage_booking")}</p>
      <h1 className="text-3xl sm:text-4xl font-bold mt-2 mb-6" data-testid="manage-service">{a.service_name}</h1>
      <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="manage-details">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-2xl font-bold">{a.date} · {a.start_time}</div>
          <StatusBadge status={a.status} testId="manage-status" />
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2"><MapPin className="h-4 w-4" />{data.location.name} · {data.location.address}</div>
        <div className="text-sm">{t("professional")}: <b>{a.professional_name}</b> · {a.duration} {t("min")}</div>
        <div className="text-sm">{t("price")}: <b>{money(a.price, a.currency, lang)}</b>{a.deposit_paid && <> · {a.pay_mode === "full" ? "Paid online" : t("deposit")} {money(a.charge_amount ?? a.deposit_amount, a.currency, lang)} ✓</>}</div>
        {a.discount_amount > 0 && <div className="text-sm" data-testid="manage-discount">Discount {a.discount_code}: −{money(a.discount_amount, a.currency, lang)}</div>}
        {a.gift_applied > 0 && <div className="text-sm" data-testid="manage-gift">Gift card {a.gift_card_code}: {money(a.gift_applied, a.currency, lang)} {a.gift_released ? "returned to your gift card" : "✓"}</div>}
      </div>
      <HomeVisitExtras token={token} lang={lang} onChanged={load} />
      {(data.branding.contact?.whatsapp || data.branding.contact?.email) && <div className="mt-6 space-y-2" data-testid="manage-contact"><p className="text-sm text-muted-foreground">Questions about your booking? Message us</p><ContactButtons contact={data.branding.contact} testPrefix="manage-contact" /></div>}
      {a.status !== "cancelled" && (
        <div className="flex flex-wrap gap-3 mt-6">
          <a href={gcalLink({ title: `${a.service_name} - ${data.branding.clinic_name || ""}`, startUtc: a.start_utc, minutes: a.duration, location: `${data.location.name} ${data.location.address}` })}
            target="_blank" rel="noreferrer" data-testid="manage-gcal-link">
            <Button variant="outline" className="rounded-full"><CalendarPlus className="h-4 w-4 mr-2" />{t("add_gcal")}</Button>
          </a>
          {data.can_cancel && <Button variant="ghost" className="rounded-full text-destructive" data-testid="manage-cancel-btn" onClick={cancel}>{t("cancel_booking")}</Button>}
        </div>
      )}
    </PublicFrame>
  );
}
